import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader,
  Button,
  CircularProgress,
  Divider,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
// Import Chart.js components directly without type imports
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import { getAuthMetrics, getSuspiciousActivityLogs } from '../../services/authLoggingService';
import { useAuth } from '../../contexts/AuthContext';

// Define chart types manually to avoid import issues
type ChartData = {
  labels: string[];
  datasets: Array<{
    label?: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    tension?: number;
  }>;
};

type ChartOptions = {
  responsive: boolean;
  plugins: {
    legend: {
      position: 'top' | 'bottom' | 'left' | 'right';
    };
    title: {
      display: boolean;
      text: string;
    };
  };
  scales?: {
    y?: {
      beginAtZero: boolean;
      title?: {
        display: boolean;
        text: string;
      };
    };
    x?: {
      title?: {
        display: boolean;
        text: string;
      };
    };
  };
};

// Mock chart components to avoid import issues
const Line = ({ data, options }: { data: ChartData; options: ChartOptions }) => (
  <div>
    <h3 style={{ textAlign: 'center' }}>{options.plugins.title.text}</h3>
    <p style={{ textAlign: 'center' }}>Chart visualization not available</p>
  </div>
);

const Bar = ({ data, options }: { data: ChartData; options: ChartOptions }) => (
  <div>
    <h3 style={{ textAlign: 'center' }}>{options.plugins.title.text}</h3>
    <p style={{ textAlign: 'center' }}>Chart visualization not available</p>
  </div>
);

const Pie = ({ data, options }: { data: ChartData; options: ChartOptions }) => (
  <div>
    <h3 style={{ textAlign: 'center' }}>{options.plugins.title.text}</h3>
    <p style={{ textAlign: 'center' }}>Chart visualization not available</p>
  </div>
);

const AuthDashboard: React.FC = () => {
  const { isAdmin } = useAuth();
  const [metrics, setMetrics] = useState<any>(null);
  const [suspiciousActivities, setSuspiciousActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [timeRange, setTimeRange] = useState<number>(30); // days
  
  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, [timeRange]);
  
  // Fetch dashboard data
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch metrics
      const metricsData = await getAuthMetrics(timeRange);
      setMetrics(metricsData);
      
      // Fetch suspicious activities (admin only)
      if (isAdmin()) {
        const activities = await getSuspiciousActivityLogs(false);
        setSuspiciousActivities(activities);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Prepare chart data for daily activity
  const getDailyActivityChartData = (): ChartData => {
    if (!metrics || !metrics.dailyActivity) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    // Sort dates and get last N days
    const sortedDates = Object.keys(metrics.dailyActivity).sort();
    const labels = sortedDates.slice(-timeRange);
    
    // Get corresponding values
    const data = labels.map(date => metrics.dailyActivity[date] || 0);
    
    return {
      labels,
      datasets: [
        {
          label: 'Authentication Events',
          data,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.2
        }
      ]
    };
  };
  
  // Prepare chart data for event types
  const getEventTypeChartData = (): ChartData => {
    if (!metrics || !metrics.eventsByType) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    const labels = Object.keys(metrics.eventsByType).map(key => 
      key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    );
    
    const data = Object.values(metrics.eventsByType) as number[];
    
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: [
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 99, 132, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(153, 102, 255, 0.6)',
            'rgba(255, 159, 64, 0.6)',
            'rgba(199, 199, 199, 0.6)',
            'rgba(83, 102, 255, 0.6)',
            'rgba(40, 159, 64, 0.6)',
            'rgba(210, 199, 199, 0.6)',
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(199, 199, 199, 1)',
            'rgba(83, 102, 255, 1)',
            'rgba(40, 159, 64, 1)',
            'rgba(210, 199, 199, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };
  
  // Prepare chart data for login success vs failure
  const getLoginStatusChartData = (): ChartData => {
    if (!metrics) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    return {
      labels: ['Login Attempts'],
      datasets: [
        {
          label: 'Successful',
          data: [metrics.successfulLogins || 0],
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
        {
          label: 'Failed',
          data: [metrics.failedLogins || 0],
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        }
      ],
    };
  };
  
  // Chart options
  const lineChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Daily Authentication Activity',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Events'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      }
    }
  };
  
  const pieChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: 'Authentication Events by Type',
      },
    }
  };
  
  const barChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Login Success vs Failure',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Count'
        }
      }
    }
  };
  
  // Handle time range change
  const handleTimeRangeChange = (days: number) => {
    setTimeRange(days);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Authentication Security Dashboard
      </Typography>
      
      {/* Time Range Selector */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
        <Button 
          variant={timeRange === 7 ? 'contained' : 'outlined'} 
          onClick={() => handleTimeRangeChange(7)}
        >
          Last 7 Days
        </Button>
        <Button 
          variant={timeRange === 30 ? 'contained' : 'outlined'} 
          onClick={() => handleTimeRangeChange(30)}
        >
          Last 30 Days
        </Button>
        <Button 
          variant={timeRange === 90 ? 'contained' : 'outlined'} 
          onClick={() => handleTimeRangeChange(90)}
        >
          Last 90 Days
        </Button>
        <Button 
          variant="outlined" 
          onClick={fetchData}
          sx={{ ml: 'auto' }}
        >
          Refresh
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Suspicious Activities Alert (Admin Only) */}
          {isAdmin() && suspiciousActivities.length > 0 && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }}
              action={
                <Button color="inherit" size="small" href="/auth-logs">
                  View Details
                </Button>
              }
            >
              <AlertTitle>Security Alert</AlertTitle>
              {suspiciousActivities.length} suspicious authentication activities detected! Review them in the logs.
            </Alert>
          )}
          
          {/* Key Metrics */}
          {metrics && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SecurityIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" color="text.secondary">Total Auth Events</Typography>
                    </Box>
                    <Typography variant="h3">{metrics.totalEvents}</Typography>
                    <Typography variant="body2" color="text.secondary">Last {timeRange} days</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LockIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6" color="text.secondary">Login Success Rate</Typography>
                    </Box>
                    <Typography variant="h3">
                      {metrics.successfulLogins && (metrics.successfulLogins + metrics.failedLogins) > 0
                        ? `${Math.round((metrics.successfulLogins / (metrics.successfulLogins + metrics.failedLogins)) * 100)}%`
                        : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {metrics.successfulLogins} successful / {metrics.failedLogins} failed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color="info" sx={{ mr: 1 }} />
                      <Typography variant="h6" color="text.secondary">New Registrations</Typography>
                    </Box>
                    <Typography variant="h3">{metrics.registrations}</Typography>
                    <Typography variant="body2" color="text.secondary">Last {timeRange} days</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WarningIcon color="warning" sx={{ mr: 1 }} />
                      <Typography variant="h6" color="text.secondary">Failed Logins</Typography>
                    </Box>
                    <Typography variant="h3">{metrics.failedLogins}</Typography>
                    <Typography variant="body2" color="text.secondary">Last {timeRange} days</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          
          {/* Charts */}
          {metrics && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Line 
                    data={getDailyActivityChartData()} 
                    options={lineChartOptions}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Pie 
                    data={getEventTypeChartData()} 
                    options={pieChartOptions}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Bar 
                    data={getLoginStatusChartData()} 
                    options={barChartOptions}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Security Recommendations</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <SecurityIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Enable Multi-Factor Authentication" 
                        secondary="Add an extra layer of security to user accounts"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LockIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Implement Account Lockout" 
                        secondary="Lock accounts after multiple failed login attempts"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <WarningIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Monitor Suspicious Activities" 
                        secondary="Regularly review authentication logs for unusual patterns"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Enforce Strong Password Policies" 
                        secondary="Require complex passwords and regular password changes"
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default AuthDashboard;